import { View, Text, FlatList, Pressable, StyleSheet, RefreshControl } from 'react-native'
import { useState, useEffect, useCallback } from 'react'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useRouter } from 'expo-router'
import ContentContainer from '../../../src/components/ContentContainer'
import { ArrowLeft, Receipt, Zap, Wifi, Smartphone, TrendingDown, FileText, Info, Upload } from 'lucide-react-native'
import { apiClient } from '../../../src/api/client'
import { useAuth } from '../../../src/auth/AuthProvider'
import BillUpload from '../../../src/components/BillUpload'
import { sentryCaptureException } from '../../../src/lib/sentry'
export { ScreenErrorBoundary as ErrorBoundary } from '../../../src/components/ScreenErrorBoundary'
import { colors, spacing, borderRadius, typography } from '../../../src/theme'
import { RegningerSkeleton } from '../../../src/components/Skeleton'

interface BillRecord {
  id: string
  category: string
  provider: string
  monthly_price: number
  currency: string
  invoice_date: string
  source: string
  confidence: string
  created_at: string
}

interface CategoryAverage {
  '3month_average_dkk': number | null
  confirmed_current_price: number | null
}

interface BillHistoryResponse {
  records: BillRecord[]
  averages: Record<string, CategoryAverage>
  total: number
}

const CATEGORY_CONFIG: Record<string, { label: string; Icon: typeof Zap; color: string; bg: string }> = {
  energy: { label: 'Strøm', Icon: Zap, color: colors.categories.energy, bg: colors.categoriesBg.energy },
  broadband: { label: 'Bredbånd', Icon: Wifi, color: colors.categories.internet, bg: colors.categoriesBg.internet },
  mobile: { label: 'Mobil', Icon: Smartphone, color: colors.categories.mobile, bg: colors.categoriesBg.mobile },
}

export default function RegningerPage() {
  const router = useRouter()
  const { user } = useAuth()
  const [data, setData] = useState<BillHistoryResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [filter, setFilter] = useState<string>('all')
  const [showUpload, setShowUpload] = useState(false)

  const loadBills = useCallback(async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const res = await apiClient.contracts.billHistory()
      setData(res.data)
    } catch (err: any) {
      sentryCaptureException(err, { screen: 'regninger' })
      setLoadError(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!user) return
    loadBills()
  }, [user])

  const records = data?.records ?? []
  const averages = data?.averages ?? {}
  const categories = Object.keys(averages)
  const filtered = filter === 'all' ? records : records.filter(r => r.category === filter)

  if (loading) {
    return <RegningerSkeleton />
  }

  return (
    <SafeAreaView style={styles.container} edges={[]}>
      <ContentContainer>
      {/* Header */}
      <View style={styles.header}>
        <Pressable onPress={() => router.back()} style={styles.backButton}>
          <ArrowLeft size={20} color={colors.text.primary} />
        </Pressable>
        <View style={{ flex: 1 }}>
          <Text style={styles.headerTitle}>Regninger</Text>
          <Text style={styles.headerSubtitle}>Dine regninger fra selskaber — opdateres automatisk</Text>
        </View>
        <Pressable
          onPress={() => setShowUpload(true)}
          style={{ padding: spacing.xs, borderRadius: borderRadius.full, backgroundColor: colors.primaryLight }}
        >
          <Upload size={20} color={colors.primary} />
        </Pressable>
      </View>

      {loadError && (
        <View style={styles.errorBanner}>
          <Info size={16} color="#92400e" />
          <Text style={styles.errorText}>Noget gik galt — vores team er notificeret.</Text>
          <Pressable onPress={loadBills}>
            <Text style={styles.errorRetry}>Prøv igen</Text>
          </Pressable>
        </View>
      )}

      <FlatList
        data={filtered}
        keyExtractor={(item) => item.id}
        contentContainerStyle={{ padding: spacing.md, paddingBottom: 100 }}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={async () => {
              setRefreshing(true)
              await loadBills()
              setRefreshing(false)
            }}
            tintColor={colors.primary}
          />
        }
        ListHeaderComponent={() => (
          <View>
            {/* 3-month averages */}
            {categories.length > 0 && (
              <View style={styles.averagesRow}>
                {categories.map(cat => {
                  const cfg = CATEGORY_CONFIG[cat] || { label: cat, Icon: Receipt, color: colors.text.secondary, bg: colors.surface }
                  const CatIcon = cfg.Icon
                  const avg = averages[cat]
                  return (
                    <View key={cat} style={styles.averageCard}>
                      <View style={styles.averageIconRow}>
                        <View style={[styles.averageIcon, { backgroundColor: cfg.bg }]}>
                          <CatIcon size={16} color={cfg.color} />
                        </View>
                        <Text style={styles.averageLabel}>{cfg.label}</Text>
                      </View>
                      {avg['3month_average_dkk'] != null ? (
                        <View>
                          <Text style={styles.averageValue}>
                            {Math.round(avg['3month_average_dkk'])} kr
                            <Text style={styles.averageUnit}>/md</Text>
                          </Text>
                          <View style={styles.averageSubRow}>
                            <TrendingDown size={10} color={colors.text.muted} />
                            <Text style={styles.averageSub}>3-måneders gennemsnit</Text>
                          </View>
                        </View>
                      ) : (
                        <Text style={styles.averageEmpty}>Ingen data endnu</Text>
                      )}
                    </View>
                  )
                })}
              </View>
            )}

            {/* Filter tabs */}
            {records.length > 0 && (
              <FlatList
                horizontal
                showsHorizontalScrollIndicator={false}
                data={[{ key: 'all', label: 'Alle' }, ...categories.map(c => ({
                  key: c,
                  label: CATEGORY_CONFIG[c]?.label || c,
                }))]}
                style={styles.filterTabs}
                contentContainerStyle={{ gap: spacing.xs }}
                keyExtractor={(item) => item.key}
                renderItem={({ item: tab }) => {
                  const isActive = filter === tab.key
                  return (
                    <Pressable
                      style={[styles.filterTab, isActive && styles.filterTabActive]}
                      onPress={() => setFilter(tab.key)}
                    >
                      <Text style={[styles.filterTabText, isActive && styles.filterTabTextActive]}>
                        {tab.label}
                      </Text>
                    </Pressable>
                  )
                }}
              />
            )}
          </View>
        )}
        ListEmptyComponent={() => (
          <View style={styles.emptyContainer}>
            <Receipt size={48} color={colors.text.muted} />
            <Text style={styles.emptyTitle}>Ingen regninger endnu</Text>
            <Text style={styles.emptyText}>
              Regninger hentes automatisk fra din tilsluttede email, når vi modtager dem fra dine selskaber.
            </Text>
          </View>
        )}
        renderItem={({ item: bill }) => {
          const cfg = CATEGORY_CONFIG[bill.category] || { label: bill.category, Icon: FileText, color: colors.text.secondary, bg: colors.surface }
          const CatIcon = cfg.Icon
          return (
            <View style={styles.billRow}>
              <View style={[styles.billIcon, { backgroundColor: cfg.bg }]}>
                <CatIcon size={18} color={cfg.color} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.billProvider} numberOfLines={1}>{bill.provider}</Text>
                <Text style={styles.billMeta}>
                  {cfg.label} · {new Date(bill.invoice_date).toLocaleDateString('da-DK')}
                </Text>
              </View>
              <View style={styles.billPriceColumn}>
                <Text style={styles.billPrice}>
                  {Math.round(bill.monthly_price).toLocaleString('da-DK')} kr/md
                </Text>
                <View style={[
                  styles.confidenceBadge,
                  { backgroundColor: bill.confidence === 'high' ? colors.successLight : colors.warningLight },
                ]}>
                  <Text style={[
                    styles.confidenceText,
                    { color: bill.confidence === 'high' ? '#15803d' : '#a16207' },
                  ]}>
                    {bill.confidence === 'high' ? 'Verificeret' : 'Estimat'}
                  </Text>
                </View>
              </View>
            </View>
          )
        }}
      />
      {showUpload && user?.id && (
        <View style={styles.uploadOverlay}>
          <View style={styles.uploadCard}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.md }}>
              <Text style={styles.headerTitle}>Upload regning</Text>
              <Pressable onPress={() => setShowUpload(false)}>
                <Text style={{ ...typography.captionBold, color: colors.primary }}>Luk</Text>
              </Pressable>
            </View>
            <BillUpload userId={user.id} onUploadComplete={() => { setShowUpload(false); loadBills() }} />
          </View>
        </View>
      )}
      </ContentContainer>
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.surface },
  header: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
    backgroundColor: colors.background, borderBottomWidth: 1, borderBottomColor: colors.border,
  },
  backButton: {
    width: 40, height: 40, alignItems: 'center', justifyContent: 'center',
    borderRadius: borderRadius.md, backgroundColor: colors.surface,
  },
  headerTitle: { ...typography.h2, color: colors.text.primary },
  headerSubtitle: { ...typography.caption, color: colors.text.secondary, marginTop: 2 },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  errorBanner: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.xs, margin: spacing.md,
    padding: spacing.sm, backgroundColor: '#fffbeb', borderRadius: borderRadius.md,
    borderWidth: 1, borderColor: '#fde68a',
  },
  errorText: { flex: 1, ...typography.small, color: '#92400e' },
  errorRetry: { ...typography.captionBold, color: '#92400e' },

  averagesRow: { flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.md },
  averageCard: {
    flex: 1, backgroundColor: colors.background, borderWidth: 1, borderColor: colors.border,
    borderRadius: borderRadius.lg, padding: spacing.md,
  },
  averageIconRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginBottom: spacing.xs },
  averageIcon: { width: 30, height: 30, borderRadius: 10, alignItems: 'center', justifyContent: 'center' },
  averageLabel: { ...typography.label, color: colors.text.secondary, fontWeight: '500' },
  averageValue: { ...typography.bodyBold, color: colors.text.primary },
  averageUnit: { fontSize: 12, fontWeight: '400', color: colors.text.muted },
  averageSubRow: { flexDirection: 'row', alignItems: 'center', gap: 3, marginTop: 4 },
  averageSub: { fontSize: 11, color: colors.text.muted },
  averageEmpty: { ...typography.caption, color: colors.text.muted },

  filterTabs: { flexGrow: 0, marginBottom: spacing.md },
  filterTab: {
    paddingHorizontal: 16, paddingVertical: 10, borderRadius: borderRadius.md,
    backgroundColor: colors.background, borderWidth: 1, borderColor: colors.border,
  },
  filterTabActive: { backgroundColor: colors.primary, borderColor: colors.primary },
  filterTabText: { ...typography.caption, color: colors.text.secondary, fontWeight: '500' },
  filterTabTextActive: { color: colors.text.inverse },

  emptyContainer: { alignItems: 'center', paddingTop: 60, gap: spacing.sm },
  emptyTitle: { ...typography.bodyBold, color: colors.text.primary },
  emptyText: { ...typography.caption, color: colors.text.muted, textAlign: 'center', maxWidth: 280 },

  billRow: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    padding: spacing.md, marginBottom: spacing.xs,
    backgroundColor: colors.background, borderRadius: borderRadius.lg,
    borderWidth: 1, borderColor: colors.border,
  },
  billIcon: { width: 40, height: 40, borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
  billProvider: { ...typography.bodyBold, color: colors.text.primary },
  billMeta: { ...typography.caption, color: colors.text.muted },
  billPriceColumn: { alignItems: 'flex-end' },
  billPrice: { ...typography.bodyBold, color: colors.text.primary },
  confidenceBadge: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: borderRadius.full, marginTop: 2 },
  confidenceText: { fontSize: 11, fontWeight: '600' },
  uploadOverlay: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center',
    padding: spacing.lg,
  },
  uploadCard: {
    backgroundColor: colors.background, borderRadius: borderRadius.xl,
    padding: spacing.lg, width: '100%', maxWidth: 480,
  },
})
